import { Controller } from "@hotwired/stimulus"
import { createConsumer } from "@rails/actioncable"
import { relativeTimeInRo } from "../helpers/time_formatter"

export default class extends Controller {
  static targets = ["container"]

  connect() {
    this.consumer = createConsumer("/cable")
    this.channel = this.consumer.subscriptions.create("ClustersChannel", {
      received: (data) => {
        this.handleUpdate(data)
      }
    })
  }

  disconnect() {
    if (this.channel) {
      this.channel.unsubscribe()
    }
    if (this.consumer) {
      this.consumer.disconnect()
    }
  }

  handleUpdate(data) {
    if (data.action === "update_cluster" && data.cluster) {
      const existingCluster = this.containerTarget.querySelector(`[data-cluster-id="${data.cluster.id}"]`)

      if (existingCluster) {
        // For updates, remove the old one and insert at top with animation
        existingCluster.remove()
        const html = this.renderCluster(data.cluster)
        this.containerTarget.insertAdjacentHTML("afterbegin", html)

        // Add slide-in animation from the top
        const updatedCluster = this.containerTarget.querySelector(`[data-cluster-id="${data.cluster.id}"]`)
        if (updatedCluster) {
          updatedCluster.style.opacity = "0"
          updatedCluster.style.transform = "translateY(-20px)"
          updatedCluster.style.transition = "opacity 0.4s ease-out, transform 0.4s ease-out"

          setTimeout(() => {
            updatedCluster.style.opacity = "1"
            updatedCluster.style.transform = "translateY(0)"
          }, 10)
        }
      } else {
        // Insert new cluster at the top
        const html = this.renderCluster(data.cluster)
        this.containerTarget.insertAdjacentHTML("afterbegin", html)

        // Add fade-in animation
        const newCluster = this.containerTarget.querySelector(`[data-cluster-id="${data.cluster.id}"]`)
        if (newCluster) {
          newCluster.style.opacity = "0"
          newCluster.style.transform = "translateY(-20px)"
          newCluster.style.transition = "opacity 0.5s ease-out, transform 0.5s ease-out"

          setTimeout(() => {
            newCluster.style.opacity = "1"
            newCluster.style.transform = "translateY(0)"
          }, 10)
        }
      }
    } else if (data.action === "delete_cluster") {
      // Remove cluster from the page with fade-out animation
      const clusterToDelete = this.containerTarget.querySelector(`[data-cluster-id="${data.cluster_id}"]`)

      if (clusterToDelete) {
        // Add fade-out animation
        clusterToDelete.style.transition = "opacity 0.3s ease-out, transform 0.3s ease-out"
        clusterToDelete.style.opacity = "0"
        clusterToDelete.style.transform = "translateX(-20px)"

        // Remove element after animation completes
        setTimeout(() => {
          clusterToDelete.remove()
        }, 300)
      }
    }
  }

  renderCluster(cluster) {
    const articles = cluster.articles || []
    const isSingleArticle = articles.length === 1

    if (isSingleArticle) {
      const article = articles[0]
      return `
        <div class="box" data-cluster-id="${cluster.id}">
          <article class="media">
            <div class="media-content">
              <div class="content">
                <p class="has-text-weight-bold">
                  ${this.escapeHtml(article.source.name)}
                </p>
                <h4 class="title is-5">
                  <a href="${this.escapeHtml(article.url)}" target="_blank" rel="noopener">
                    ${this.escapeHtml(article.title)}
                  </a>
                </h4>
                <p class="subtitle is-6 has-text-grey">
                  ${relativeTimeInRo(article.published_at)}
                </p>
              </div>
            </div>
          </article>
        </div>
      `
    } else {
      const articlesList = articles.map(article => `
        <li class="mb-2">
          <span class="has-text-weight-bold">${this.escapeHtml(article.source.name)}</span>
          <span class="mx-2">
            <a href="${this.escapeHtml(article.url)}" target="_blank" rel="noopener">
              ${this.escapeHtml(article.title)}
            </a>
          </span>
          <span class="has-text-grey is-size-7">
            ${relativeTimeInRo(article.published_at)}
          </span>
        </li>
      `).join('')

      return `
        <div class="box" data-cluster-id="${cluster.id}">
          <article class="media">
            <div class="media-content">
              <div class="content">
                <h4 class="title is-5">
                  ${this.escapeHtml(cluster.name)}
                </h4>
                <ul class="ml-4">
                  ${articlesList}
                </ul>
              </div>
            </div>
          </article>
        </div>
      `
    }
  }

  escapeHtml(text) {
    if (!text) return ''
    const map = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;'
    }
    return text.replace(/[&<>"']/g, (m) => map[m])
  }
}
